/**
 * The inspection list behind the analytics page, and the filters over it.
 *
 * Kept free of React and axios so the filtering can be tested directly — the
 * charts above it are derived aggregates, but this is the actual evidence, and
 * a filter that quietly drops rows is worse than no filter.
 *
 * Every field here was taken from live `GET /get_analytics_data` responses on
 * both Ergo hosts. What the rows really carry, and how often:
 *
 *   ref_num             always            num_plate      motor identity
 *   created_at          always            address        property identity
 *   claim_state         always (8/6/3)    case_status    always (passed/failed)
 *   photos_uploaded     always            warnings       nearly always
 *   inspection_duration ~1 in 3           damaged_parts  motor only
 *   trinetra_rating     rare              trinetra_comment  rarer still
 *
 * So `rating` and `comment` are real fields that are usually absent. They get
 * filters anyway — that is exactly how you find the few rows that have them —
 * but the facet counts say how many rows carry each, rather than presenting an
 * empty filter as an empty result set.
 */

import { labelWarnings, prettyEnum } from "./analyticsData";

const asArray = (value) => (Array.isArray(value) ? value : []);

const text = (value) =>
  typeof value === "string" && value.trim() ? value.trim() : null;

const positive = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/** "2026-09-01 13:20:44" -> "2026-09-01" */
const dayOf = (value) =>
  typeof value === "string" && value.length >= 10 ? value.slice(0, 10) : null;

// Nice labels for the countries Ergo Baltic actually operates in. Anything else
// is title-cased rather than folded into "Unknown": a new country should appear
// as itself, not disappear.
const COUNTRY_LABELS = {
  lithuania: "Lithuania",
  latvia: "Latvia",
  estonia: "Estonia",
};

const NOT_A_COUNTRY = new Set(["test", "demo", "staging", "prod", "dev"]);

export const UNKNOWN_COUNTRY = { key: "unknown", label: "Unknown" };

/**
 * Country, read out of the reference number.
 *
 * The API returns no country field, but every ref_num observed on both hosts
 * carries one in its prefix:
 *
 *   motor_lithuania-20262911661        prpt_estonia_test-1787656048572
 *   motor_latvia-1787731445280         apartment_lithuania-20262434859
 *
 * So the shape is <line>_<country>[_marker]-<id>: the country is the second
 * underscore-separated token of the part before the first hyphen. This is a
 * derivation, not a declared field, and the UI says so — a ref_num that does
 * not follow the convention reports Unknown instead of guessing.
 */
export const countryOf = (refNum) => {
  const prefix = String(refNum || "").split("-")[0];
  const token = prefix.split("_")[1]?.toLowerCase();
  if (!token || NOT_A_COUNTRY.has(token) || !/^[a-z]{3,}$/.test(token)) {
    return UNKNOWN_COUNTRY;
  }
  return {
    key: token,
    label: COUNTRY_LABELS[token] || token[0].toUpperCase() + token.slice(1),
  };
};

/** Reference numbers on both hosts mark non-production cases with _test. */
export const isTestRef = (refNum) =>
  String(refNum || "")
    .split("-")[0]
    .split("_")
    .some((part) => NOT_A_COUNTRY.has(part.toLowerCase()));

/**
 * One row per inspection, with everything a filter or a column needs.
 *
 * `identityField` comes from the client file — num_plate for motor, address for
 * property — so this does not have to know which line it is looking at.
 */
export const toInspections = (raw, { identityField = "num_plate" } = {}) =>
  asArray(raw?.data).map((row) => {
    const warnings = asArray(row?.warnings)
      .map((warning) => ({
        code: text(warning?.warning_code),
        message: text(warning?.warning_message),
      }))
      .filter((warning) => warning.code);

    const parts = asArray(row?.damaged_parts)
      .map((part) => ({
        name: text(part?.partName),
        label: prettyEnum(text(part?.partName)),
        types: asArray(part?.damType).map(text).filter(Boolean),
        typeLabels: asArray(part?.damType)
          .map(text)
          .filter(Boolean)
          .map(prettyEnum),
      }))
      .filter((part) => part.name);

    const country = countryOf(row?.ref_num);

    return {
      refNum: text(row?.ref_num) || "—",
      identity: text(row?.[identityField]) || text(row?.num_plate) || text(row?.address),
      createdAt: text(row?.created_at),
      day: dayOf(row?.created_at),
      // Two different things, deliberately not merged: `case_status` is the AI
      // verdict on the capture, `claim_state` is where the claim sits in the
      // review workflow. A row can be passed and still be mid-review.
      result: text(row?.case_status),
      reviewState: row?.claim_state === undefined ? null : String(row.claim_state),
      failureReason: text(row?.failure_reason) || text(row?.failure_reason_category),
      country,
      isTest: isTestRef(row?.ref_num),
      duration: positive(row?.inspection_duration),
      photos: positive(row?.photos_uploaded) || 0,
      rating: positive(row?.trinetra_rating),
      comment: text(row?.trinetra_comment),
      warnings,
      warningCodes: warnings.map((warning) => warning.code),
      parts,
      partNames: parts.map((part) => part.name),
      damageTypes: [...new Set(parts.flatMap((part) => part.types))],
    };
  });

export const EMPTY_FILTERS = {
  query: "",
  country: "all",
  result: "all",
  reviewState: "all",
  warning: "all",
  rating: "all",
  comment: "all",
  part: "all",
  day: null,
};

export const isFilterActive = (filters) =>
  Object.entries(EMPTY_FILTERS).some(
    ([key, empty]) => (filters?.[key] ?? empty) !== empty,
  );

const counted = (map, labelFor) =>
  [...map.entries()]
    .map(([key, count]) => ({ key, count, label: labelFor ? labelFor(key) : key }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));

const tally = (rows, pick) => {
  const counts = new Map();
  rows.forEach((row) => {
    asArray(pick(row)).forEach((key) => {
      if (key === null || key === undefined) return;
      counts.set(key, (counts.get(key) || 0) + 1);
    });
  });
  return counts;
};

/**
 * What can be filtered on, with how many rows each option holds.
 *
 * Computed over ALL rows read, not the currently filtered ones, so the numbers
 * do not shift underneath the reader as they narrow — and so an option that
 * would return nothing is visibly zero rather than missing.
 */
export const facetsOf = (inspections, { reviewLabel } = {}) => {
  const rows = asArray(inspections);
  const warningMessages = new Map();
  rows.forEach((row) =>
    row.warnings.forEach((warning) => {
      if (warning.message && !warningMessages.has(warning.code)) {
        warningMessages.set(warning.code, warning.message);
      }
    }),
  );

  return {
    total: rows.length,
    countries: counted(
      tally(rows, (row) => [row.country.key]),
      (key) => (rows.find((row) => row.country.key === key) || {}).country?.label || key,
    ),
    results: counted(tally(rows, (row) => [row.result].filter(Boolean))),
    reviewStates: counted(
      tally(rows, (row) => [row.reviewState].filter(Boolean)),
      (key) => (reviewLabel ? reviewLabel(key) : key),
    ),
    // Codes stay the filter's value — they are what identifies a warning — but
    // nothing in the UI shows them any more, so each option carries a readable
    // label instead.
    warnings: labelWarnings(
      counted(
        tally(rows, (row) => [...new Set(row.warningCodes)]),
        (code) => warningMessages.get(code) || code,
      ).map((entry) => ({
        key: entry.key,
        count: entry.count,
        code: entry.key,
        message: entry.label,
      })),
    ),
    parts: counted(
      tally(rows, (row) => [...new Set(row.partNames)]),
      (key) => prettyEnum(key),
    ),
    // Ratings are 1-5 and rare; the counts are what tell a reader whether the
    // filter is worth touching at all.
    ratings: counted(tally(rows, (row) => (row.rating ? [String(Math.round(row.rating))] : []))).sort(
      (a, b) => Number(b.key) - Number(a.key),
    ),
    withRating: rows.filter((row) => row.rating).length,
    withComment: rows.filter((row) => row.comment).length,
    withWarning: rows.filter((row) => row.warnings.length).length,
    withDuration: rows.filter((row) => row.duration).length,
    days: counted(tally(rows, (row) => [row.day].filter(Boolean))).sort((a, b) =>
      a.key.localeCompare(b.key),
    ),
  };
};

const matchesQuery = (row, query) => {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  return [row.refNum, row.identity, row.comment, row.failureReason]
    .filter(Boolean)
    .some((value) => value.toLowerCase().includes(needle));
};

export const applyFilters = (inspections, filters = {}) => {
  const f = { ...EMPTY_FILTERS, ...filters };
  return asArray(inspections).filter((row) => {
    if (!matchesQuery(row, f.query)) return false;
    if (f.country !== "all" && row.country.key !== f.country) return false;
    if (f.result !== "all" && row.result !== f.result) return false;
    if (f.reviewState !== "all" && row.reviewState !== f.reviewState) return false;
    if (f.day && row.day !== f.day) return false;
    if (f.part !== "all" && !row.partNames.includes(f.part)) return false;

    if (f.warning === "none" && row.warnings.length) return false;
    if (f.warning === "any" && !row.warnings.length) return false;
    if (!["all", "any", "none"].includes(f.warning) && !row.warningCodes.includes(f.warning)) {
      return false;
    }

    if (f.rating === "rated" && !row.rating) return false;
    if (f.rating === "unrated" && row.rating) return false;
    if (!["all", "rated", "unrated"].includes(f.rating)) {
      if (!row.rating || Math.round(row.rating) !== Number(f.rating)) return false;
    }

    if (f.comment === "with" && !row.comment) return false;
    if (f.comment === "without" && row.comment) return false;

    return true;
  });
};

export const SORT_KEYS = {
  date: (row) => row.createdAt,
  ref: (row) => row.refNum,
  identity: (row) => row.identity,
  country: (row) => row.country.label,
  result: (row) => row.result,
  reviewState: (row) => row.reviewState,
  photos: (row) => row.photos,
  duration: (row) => row.duration,
  rating: (row) => row.rating,
  warnings: (row) => row.warnings.length,
  parts: (row) => row.parts.length,
};

/**
 * Sorted, with unrecorded values always last.
 *
 * Deliberate: a third of rows have no duration and almost none have a rating,
 * so letting nulls take the top of a descending sort would bury the rows the
 * reader asked to see under rows that have no value at all.
 */
export const sortInspections = (inspections, { key = "date", dir = "desc" } = {}) => {
  const pick = SORT_KEYS[key] || SORT_KEYS.date;
  const factor = dir === "asc" ? 1 : -1;
  return [...asArray(inspections)].sort((a, b) => {
    const left = pick(a);
    const right = pick(b);
    const leftEmpty = left === null || left === undefined || left === "";
    const rightEmpty = right === null || right === undefined || right === "";
    if (leftEmpty && rightEmpty) return 0;
    if (leftEmpty) return 1;
    if (rightEmpty) return -1;
    if (typeof left === "number" && typeof right === "number") {
      return (left - right) * factor;
    }
    return String(left).localeCompare(String(right)) * factor;
  });
};

// Lives in utils now — the cases list needs the same links.
export { caseHrefFor } from "../utils/caseHref";
